/* eslint-disable react/jsx-no-bind */
import {
  Box,
  Card,
  CardContent,
  Divider,
  useTheme,
  CircularProgress,
  Typography,
  Grid,
  Paper,
  Chip,
  Button,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { useForm, Controller } from 'react-hook-form';

import { tokens } from '../../theme';
import GeoMap from '../GeoMap';

import bookingDashboardService from './bookingDashboardService';


// Extend dayjs with plugins
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const statusMapping = {
  CRT: 'Created',
  ASG: 'Assigned',
  PKP: 'Pickup',
  INT: 'In Transit',
  DLD: 'Delivered',
  CLD: 'Canceled',
  RFP: 'Ready for Pickup',
  ICR: 'Incident Reported',
  NTY: 'Notify',
};

const statusColors = {
  CRT: 'rgba(54, 162, 235, 0.7)',
  ASG: 'rgba(255, 206, 86, 0.7)',
  PKP: 'rgba(75, 192, 192, 0.7)',
  INT: 'rgba(153, 102, 255, 0.7)',
  DLD: 'rgba(75, 192, 86, 0.7)',
  CLD: 'rgba(255, 99, 132, 0.7)',
  RFP: 'rgba(255, 159, 64, 0.7)',
  ICR: 'rgba(199, 199, 199, 0.7)',
  NTY: 'rgba(83, 102, 255, 0.7)',
};

const BookingDashboard = () => {
  const [, setAllBookingAnalyticsData] = useState([]);
  const [shipmentStats, setShipmentStats] = useState([]);
  const [loading, setLoading] = useState({
    shipments: true,
    trips: true,
  });
  const [error, setError] = useState({
    shipments: null,
    trips: null,
  });

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const {
    control: bookingDashboardFormControl,
    watch: watchBookingDashboardForm,
    formState: { errors: bookingDashboardFormErrors },
  } = useForm({
    defaultValues: {
      fromDate: null,
      endDate: null,
    },
    mode: 'onChange',
  });

  // Date watching and filter logic
  const fromDate = watchBookingDashboardForm('fromDate');
  const endDate = watchBookingDashboardForm('endDate');

  const fetchData = async () => {
    try {
      setLoading({ shipments: true, trips: true });
      setError({ shipments: null, trips: null });

      // const [shipmentResponse, tripResponse] = await Promise.all([
      //   bookingDashboardService.fetchBookingStats(),
      //   bookingDashboardService.fetchTripStats(),
      // ]);
      const params = {
        from_date: fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : '',
        to_date: endDate ? dayjs(endDate).format('YYYY-MM-DD') : '',
      };
      const [bookingAnalyticsResponse] = await Promise.all([
        bookingDashboardService.fetchBookingAnalytics(params),
      ]);

      // console.log("API Response:", bookingAnalyticsResponse);
      // console.log("Response data:", bookingAnalyticsResponse.data);

      // Handle different response structures
      let responseData = bookingAnalyticsResponse.data;
      if (responseData?.data && Array.isArray(responseData.data)) {
        responseData = responseData.data;
      }

      const analyticsData = Array.isArray(responseData) ? responseData : [];

      // console.log("Analytics data to set:", analyticsData);

      setAllBookingAnalyticsData(analyticsData);
      setShipmentStats(analyticsData);
      // setTripStats(tripResponse.data.status_counts || []);
    } catch (err) {
      setError({
        shipments:
          err.response?.data?.message || 'Failed to load bookings data',
        trips: err.response?.data?.message || 'Failed to load trip data',
      });
      // console.error("Error fetching data:", err);
      // Set empty arrays on error to prevent reduce errors
      setAllBookingAnalyticsData([]);
      setShipmentStats([]);
    } finally {
      setLoading({ shipments: false, trips: false });
    }
  };

  const handleSearch = () => {
    // Validate dates before searching
    if (!fromDate || !endDate) {
      return;
    }
    fetchData();
  };

  // Call API on initial load
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculateTotal = (stats) => {
    if (!Array.isArray(stats) || stats.length === 0) return 0;

    return stats.reduce((sum, item) => {
      const count = item?.count || 0;
      return sum + count;
    }, 0);
  };

  const prepareChartData = (stats, title) => {
    // console.log("prepareChartData called with:", stats, title);

    if (!stats || !Array.isArray(stats) || stats.length === 0) {
      // console.log("No stats data, returning empty chart");
      return {
        labels: ['No data available'],
        datasets: [
          {
            label: title,
            data: [0],
            backgroundColor: ['rgba(200, 200, 200, 0.7)'],
          },
        ],
      };
    }

    const sortedStats = [...stats].sort(
      (a, b) => (b.count || 0) - (a.count || 0),
    );
    // console.log("Sorted stats:", sortedStats);

    const chartData = {
      labels: sortedStats.map(
        (stat) => statusMapping[stat.status] || stat.status,
      ),
      datasets: [
        {
          label: title,
          data: sortedStats.map((stat) => stat.count || 0),
          backgroundColor: sortedStats.map(
            (stat) => statusColors[stat.status] || '#cccccc',
          ),
          borderColor: sortedStats.map((stat) =>
            statusColors[stat.status]
              ? statusColors[stat.status].replace('0.7', '1')
              : '#000000',
          ),
          borderWidth: 1,
        },
      ],
    };

    // console.log("Chart data prepared:", chartData);
    return chartData;
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.raw}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Count',
        },
      },
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };

  useEffect(() => {
    // console.log("shipmentStats updated:", shipmentStats);
  }, [shipmentStats]);
  return (
    <Box sx={{ p: 2 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h5">Booking Dashboard</Typography>

            <Box display="flex" gap={2}>
              <Grid container spacing={2} sx={{ mb: 3 }} display={'flex'}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      From Date*
                    </Typography>
                  </Box>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Controller
                      name="fromDate"
                      control={bookingDashboardFormControl}
                      rules={{
                        required: 'From date is required',
                        validate: (value) => {
                          if (!value) {
                            return 'From date is required';
                          }

                          const endDateValue =
                            watchBookingDashboardForm('endDate');

                          // Check if from date is after or equal to end date
                          if (
                            value &&
                            endDateValue &&
                            dayjs(value).isSameOrAfter(
                              dayjs(endDateValue),
                              'day',
                            )
                          ) {
                            return 'From date must be before To date';
                          }

                          return true;
                        },
                      }}
                      render={({ field: { value, onChange } }) => {
                        const endDateValue =
                          watchBookingDashboardForm('endDate');
                        return (
                          <DatePicker
                            value={value}
                            onChange={(newDate) => onChange(newDate)}
                            format="DD/MM/YYYY"
                            maxDate={
                              endDateValue
                                ? dayjs(endDateValue).subtract(1, 'day')
                                : null
                            }
                            slotProps={{
                              textField: {
                                size: 'small',
                                fullWidth: true,
                                error: !!bookingDashboardFormErrors.fromDate,
                                helperText:
                                  bookingDashboardFormErrors.fromDate?.message,
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: '40px',
                                borderRadius: '8px',
                                backgroundColor: 'white',
                                '&.Mui-focused fieldset': {
                                  borderColor: colors.swiftcarry[100],
                                  borderWidth: '2px',
                                },
                                '&:hover fieldset': {
                                  borderColor: colors.swiftcarry[100],
                                },
                              },
                              '& .MuiInputBase-input': {
                                fontSize: '14px',
                                padding: '10px 14px',
                              },
                            }}
                          />
                        );
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      To Date*
                    </Typography>
                  </Box>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Controller
                      name="endDate"
                      control={bookingDashboardFormControl}
                      rules={{
                        required: 'To date is required',
                        validate: (value) => {
                          if (!value) {
                            return 'To date is required';
                          }

                          const fromDateValue =
                            watchBookingDashboardForm('fromDate');

                          // Check if to date is before or equal to from date
                          if (
                            value &&
                            fromDateValue &&
                            dayjs(value).isSameOrBefore(
                              dayjs(fromDateValue),
                              'day',
                            )
                          ) {
                            return 'To date must be after From date';
                          }

                          return true;
                        },
                      }}
                      render={({ field: { value, onChange } }) => {
                        const fromDateValue =
                          watchBookingDashboardForm('fromDate');
                        return (
                          <DatePicker
                            value={value}
                            onChange={(newDate) => onChange(newDate)}
                            format="DD/MM/YYYY"
                            minDate={
                              fromDateValue
                                ? dayjs(fromDateValue).add(1, 'day')
                                : null
                            }
                            slotProps={{
                              textField: {
                                size: 'small',
                                fullWidth: true,
                                error: !!bookingDashboardFormErrors.endDate,
                                helperText:
                                  bookingDashboardFormErrors.endDate?.message,
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: '40px',
                                borderRadius: '8px',
                                backgroundColor: 'white',
                                '&.Mui-focused fieldset': {
                                  borderColor: colors.swiftcarry[100],
                                  borderWidth: '2px',
                                },
                                '&:hover fieldset': {
                                  borderColor: colors.swiftcarry[100],
                                },
                              },
                              '& .MuiInputBase-input': {
                                fontSize: '14px',
                                padding: '10px 14px',
                              },
                            }}
                          />
                        );
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      &nbsp;
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={!fromDate || !endDate || loading.shipments}
                    sx={{
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: colors.swiftcarry[100],
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 500,
                      width: '100%',
                      '&:hover': {
                        backgroundColor: colors.swiftcarry[200],
                      },
                      '&:disabled': {
                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                        color: 'rgba(0, 0, 0, 0.26)',
                      },
                    }}
                  >
                    {loading.shipments ? 'Searching...' : 'Search'}
                  </Button>
                </Grid>
              </Grid>
              {/* <FormControl size="small" sx={{ minWidth: 120, mt: "24px" }}>
                <Select value={timeFilter} onChange={handleTimeFilterChange}>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
              {(timeFilter === "monthly" || timeFilter === "yearly") && (
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={subOption}
                    onChange={(e) => setSubOption(e.target.value)}
                  >
                    {timeFilter === "monthly"
                      ? Array.from({ length: 12 }, (_, i) => (
                          <MenuItem key={i + 1} value={i + 1}>
                            {new Date(0, i).toLocaleString("default", {
                              month: "short",
                            })}
                          </MenuItem>
                        ))
                      : Array.from({ length: 5 }, (_, i) => (
                          <MenuItem
                            key={new Date().getFullYear() - i}
                            value={new Date().getFullYear() - i}
                          >
                            {new Date().getFullYear() - i}
                          </MenuItem>
                        ))}
                  </Select>
                </FormControl>
              )} */}
            </Box>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6">
                    Bookings{' '}
                    {fromDate ? dayjs(fromDate).format('DD/MM/YYYY') : ''}{' '}
                    {fromDate ? '-' : ''}{' '}
                    {endDate ? dayjs(endDate).format('DD/MM/YYYY') : ''}
                  </Typography>
                  <Chip
                    label={`Total: ${calculateTotal(shipmentStats)}`}
                    color="primary"
                    size="small"
                  />
                </Box>

                <Box sx={{ height: 350 }}>
                  {loading.shipments ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="100%"
                    >
                      <CircularProgress />
                    </Box>
                  ) : error.shipments ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="100%"
                    >
                      <Typography color="error">{error.shipments}</Typography>
                    </Box>
                  ) : (
                    <Bar
                      data={prepareChartData(shipmentStats, 'Bookings')}
                      options={chartOptions}
                    />
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6">Trips Status</Typography>
                  <Chip
                    label={`Total: ${calculateTotal(tripStats)}`}
                    color="secondary"
                    size="small"
                  />
                </Box>
                <Box sx={{ height: 350 }}>
                  {loading.trips ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="100%"
                    >
                      <CircularProgress />
                    </Box>
                  ) : error.trips ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="100%"
                    >
                      <Typography color="error">{error.trips}</Typography>
                    </Box>
                  ) : (
                    <Bar
                      data={prepareChartData(tripStats, "Trips")}
                      options={chartOptions}
                    />
                  )}
                </Box>
              </Paper>
            </Grid> */}
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Geo Distribution
          </Typography>
          <Box>
            <GeoMap />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BookingDashboard;
